import axios, { AxiosInstance, AxiosResponse } from 'axios';

export type LoyaltyCampaignType = 'INACTIVITY' | 'WEEKLY_STREAK' | 'ORDER_COUNT';

export interface BaseLoyaltyCampaign {
  id: string;
  cnpj: string;
  type: LoyaltyCampaignType;
  name: string;
  description?: string;
  active: boolean;
  triggerHour?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InactivityCampaignConfig {
  thresholdDays: number;
  couponValue: number;
  couponMinOrderValue?: number;
  couponValidityDays: number;
  messageTemplateId?: string;
  messageTemplateBody?: string;
  rewardDescription?: string;
}

export interface WeeklyStreakCampaignConfig {
  requiredWeeks: number;
  bonusItemDescription?: string;
  couponValue?: number;
  couponMinOrderValue?: number;
  rewardValidityDays?: number;
  messageTemplateId?: string;
  messageTemplateBody?: string;
  rewardDescription?: string;
}

export type LoyaltyRewardType = 'COUPON' | 'ITEM' | 'CUSTOM';

export interface OrderCountCampaignConfig {
  ordersRequired: number;
  rewardType: LoyaltyRewardType;
  rewardValue?: string;
  couponValue?: number;
  couponMinOrderValue?: number;
  rewardValidityDays?: number;
  ordersWindowDays?: number;
  messageTemplateId?: string;
  messageTemplateBody?: string;
  rewardDescription?: string;
}

export type LoyaltyCampaignConfig =
  | InactivityCampaignConfig
  | WeeklyStreakCampaignConfig
  | OrderCountCampaignConfig;

export interface LoyaltyCampaign extends BaseLoyaltyCampaign {
  config: LoyaltyCampaignConfig;
}

export interface ListCampaignsParams {
  type?: LoyaltyCampaignType;
  activeOnly?: boolean;
  cnpj?: string;
}

export interface CreateCampaignPayload {
  type: LoyaltyCampaignType;
  name: string;
  description?: string;
  triggerHour?: string;
  active?: boolean;
  config: LoyaltyCampaignConfig;
}

export interface UpdateCampaignPayload {
  name?: string;
  description?: string;
  triggerHour?: string;
  active?: boolean;
  config?: LoyaltyCampaignConfig;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const withAuthInterceptor = (client: AxiosInstance): AxiosInstance => {
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  return client;
};

class LoyaltyCampaignService {
  private client: AxiosInstance;

  constructor() {
    this.client = withAuthInterceptor(
      axios.create({
        baseURL: API_BASE_URL,
        timeout: 15000,
      }),
    );
  }

  private buildCnpjParam(cnpj?: string) {
    if (!cnpj) {
      return undefined;
    }
    const digits = cnpj.replace(/\D/g, '');
    return digits ? { cnpj: digits } : undefined;
  }

  async listCampaigns(params?: ListCampaignsParams): Promise<LoyaltyCampaign[]> {
    try {
      const response: AxiosResponse<LoyaltyCampaign[]> = await this.client.get('/admin/loyalty/campaigns', {
        params: {
          type: params?.type,
          activeOnly: params?.activeOnly,
          ...this.buildCnpjParam(params?.cnpj),
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao listar campanhas de fidelidade');
    }
  }

  async createCampaign(payload: CreateCampaignPayload, options?: { cnpj?: string }): Promise<LoyaltyCampaign> {
    try {
      const response: AxiosResponse<LoyaltyCampaign> = await this.client.post(
        '/admin/loyalty/campaigns',
        payload,
        {
          params: this.buildCnpjParam(options?.cnpj),
        },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao criar campanha de fidelidade');
    }
  }

  async updateCampaign(
    id: string,
    payload: UpdateCampaignPayload,
    options?: { cnpj?: string },
  ): Promise<LoyaltyCampaign> {
    try {
      const response: AxiosResponse<LoyaltyCampaign> = await this.client.put(
        `/admin/loyalty/campaigns/${id}`,
        payload,
        {
          params: this.buildCnpjParam(options?.cnpj),
        },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar campanha de fidelidade');
    }
  }

  async deleteCampaign(id: string, options?: { cnpj?: string }): Promise<void> {
    try {
      await this.client.delete(`/admin/loyalty/campaigns/${id}`, {
        params: this.buildCnpjParam(options?.cnpj),
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao remover campanha de fidelidade');
    }
  }
}

export const loyaltyCampaignService = new LoyaltyCampaignService();
export default loyaltyCampaignService;
